import type { PixelConfig } from './types';
import { isSafeString, sanitizeSource } from './utils';
import {
  Sources,
  sourceFromClickId,
  sourceFromReferrer,
  sourceFromUtms,
  withUtmDetail,
  type UtmDetail,
} from './sources';

function isInternalReferrer(internalDomains: string[]): boolean {
  if (typeof document === 'undefined') return false;
  const ref = document.referrer;
  if (!ref) return false;
  try {
    const host = new URL(ref).hostname.toLowerCase();
    return internalDomains.some((d) => host === d || host.endsWith('.' + d));
  } catch {
    return false;
  }
}

/**
 * Determine the most likely attribution source for the current page view.
 * Returns null if this page view shouldn't add a touchpoint (e.g. internal
 * navigation with no explicit source).
 *
 * Every non-null return is a canonical TDG_* label (see sources.ts) except
 * the explicit-override path, which is operator-controlled. Raw UTM values
 * are captured separately by utms.ts and surfaced in their own form fields —
 * they are never joined into the attribution label.
 */
export function detectAttribution(config: PixelConfig): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);

  // 1) Explicit attribution query param
  const explicit =
    params.get('Formstack_Submissions__c.Attribution_Type__c') ??
    params.get('attribution');
  if (explicit && isSafeString(explicit)) return sanitizeSource(explicit);

  // Safe UTM values from the URL (unsafe ones are dropped individually).
  const safe = (k: string): string | null => {
    const v = params.get(k);
    return v && isSafeString(v) ? v : null;
  };
  const utms: UtmDetail = {
    source: safe('utm_source'),
    medium: safe('utm_medium'),
    campaign: safe('utm_campaign'),
    content: safe('utm_content'),
    term: safe('utm_term'),
    id: safe('utm_id'),
  };

  // 2) Platform click identifiers (gclid, fbclid, li_fat_id, ...). These are
  //    appended by the ad platform and beat UTMs, which are hand-entered and
  //    may not name the platform (e.g. utm_source=tdg).
  const fromClickId = sourceFromClickId(params);
  if (fromClickId) return withUtmDetail(fromClickId, utms, config.attributionDetail);

  // 3) UTMs → canonical channel label (+ optional campaign/full detail)
  if (utms.source || utms.medium) {
    const fromUtm = sourceFromUtms(utms.source, utms.medium);
    if (fromUtm) return withUtmDetail(fromUtm, utms, config.attributionDetail);
  }

  // 4) Internal navigation — don't record a touchpoint
  if (isInternalReferrer(config.internalDomains)) return null;

  // 5) External referrer
  const ref = typeof document !== 'undefined' ? document.referrer : '';
  const fromRef = sourceFromReferrer(ref);
  if (fromRef) return fromRef;

  // 6) No referrer and no marketing params — direct/organic visit.
  //    Internal navigation was already filtered out in step 4, so this is
  //    a real entry event: typed URL, bookmark, email-client link, app, etc.
  return Sources.direct;
}
